import { useEffect, useRef } from 'react';
import useSocket from './useSocket';

export default function useSocketSubscription(resource, resourceIds = null, requiredResources = false, fromTimestamp = null) {
  const { socket, connectionState } = useSocket();
  const isConnected = connectionState.status === 'connected';
  const previousResourceIds = useRef(null);

  useEffect(() => {
    if (!isConnected || !resource) return;
    if (requiredResources && resourceIds === null) return;

    // If we have previous IDs, unsubscribe from the ones that are no longer present
    if (previousResourceIds.current) {
      const removedIds = previousResourceIds.current.filter(id => 
        !resourceIds || !resourceIds.includes(id)
      );
      if (removedIds.length > 0) {
        socket.unsubscribeFromResource(resource, removedIds);
        
        // If we're unsubscribing from all previous IDs, 
        // clear previousResourceIds to prevent future unsubscribe attempts
        if (removedIds.length === previousResourceIds.current.length) {
          previousResourceIds.current = null;
        }
      }
    }

    // Subscribe to new IDs
    if (resourceIds) {
      const newIds = resourceIds.filter(id => 
        !previousResourceIds.current || !previousResourceIds.current.includes(id)
      );
      if (newIds.length > 0) {
        const fromDt = fromTimestamp ?? null;
        socket.subscribeToResource(resource, newIds, fromDt);
      }
    } else {
      // Resource-level subscription (no specific IDs)
      const fromDt = fromTimestamp ?? null;
      socket.subscribeToResource(resource, null, fromDt);
    }

    // Update previous IDs
    previousResourceIds.current = resourceIds;

    // Cleanup: unsubscribe from all IDs when component unmounts
  }, [socket, isConnected, resource, resourceIds, requiredResources, fromTimestamp]);

  useEffect(() => {
    return () => {
      if (!socket || !resource) return;
      
      if (previousResourceIds.current) {
        socket.unsubscribeFromResource(resource, previousResourceIds.current);
        // Clear the reference after unsubscribing to prevent future issues
        previousResourceIds.current = null;
      } else {
        socket.unsubscribeFromResource(resource);
      }
    };
  }, [socket, resource]);

  return isConnected;
}
